import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, Flame, DollarSign, Phone, ArrowRight, CheckCircle2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6 } }),
};

const HERO_BG = "https://images.pexels.com/photos/3334038/pexels-photo-3334038.jpeg?auto=compress&cs=tinysrgb&w=1920";
const IMG_ELEC = "https://images.pexels.com/photos/9875683/pexels-photo-9875683.jpeg?auto=compress&cs=tinysrgb&w=600";
const IMG_GAS = "https://images.pexels.com/photos/5813742/pexels-photo-5813742.jpeg?auto=compress&cs=tinysrgb&w=600";
const IMG_BUNDLE = "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&w=600";
const IMG_BIZ = "https://images.unsplash.com/photo-1626863905121-3b0c0ed7b94c?w=800&q=80";

const SERVICES = [
  {
    icon: Zap,
    title: "Residential Electricity",
    desc: "We'll find you a better electricity plan that saves you real money on your bills. Whether you're a homeowner or renter, we do the hard work for you.",
    benefits: ["Find a plan suited to your usage patterns", "No cost to compare or switch", "Expert guidance throughout the process", "We handle the entire switching process", "Ongoing plan monitoring for future savings"],
    savings: "Up to $580/yr",
    link: "electricity",
    image: IMG_ELEC,
  },
  {
    icon: DollarSign,
    title: "Business Electricity",
    desc: "Specialised electricity comparison for businesses of all sizes. From small cafes to large commercial operations, we negotiate better rates and find plans that suit your business needs.",
    benefits: ["Multi-site energy management", "Contract negotiation expertise", "Tailored business rates", "Dedicated account management", "No interruption to your supply"],
    savings: "Up to $2,400/yr",
    link: "business-energy",
    image: IMG_BUNDLE,
  },
];

const BUSINESS_SERVICES = [
  { icon: Building2, title: "Small Business Energy", desc: "Tailored electricity comparison for small businesses, cafes, retail shops, and offices.", link: "business-energy" },
  { icon: Building2, title: "Commercial Energy", desc: "Multi-site energy management and contract negotiation for medium to large businesses.", link: "business-energy" },
  { icon: Building2, title: "Industrial Energy", desc: "Specialised energy solutions for manufacturing, warehousing, and industrial operations.", link: "business-energy" },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero with background */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2E2E33]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-3xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6600]/15 text-[#FF6600] text-sm font-semibold mb-6">
              <Zap className="w-4 h-4" /> Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-5 tracking-tight">
              Electricity Comparison
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              We compare electricity plans from Australia's top providers to help you find a better deal. Free, fast, and no obligation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service blocks with images */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {SERVICES.map((s, i) => (
            <motion.div
              key={i}
              className="bg-white border-2 border-[#E2E8F0] rounded-3xl overflow-hidden card-lift"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              data-testid={`service-block-${s.link}`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-5 gap-0 ${i % 2 === 1 ? "lg:grid-flow-dense" : ""}`}>
                <div className={`lg:col-span-2 ${i % 2 === 1 ? "lg:col-start-4" : ""}`}>
                  <img src={s.image} alt={s.title} className="w-full h-64 lg:h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-[#2E2E33] flex items-center justify-center">
                      <s.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-extrabold text-[#2E2E33] text-2xl">{s.title}</h3>
                    </div>
                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#10B981]/10 text-[#10B981] text-sm font-bold">
                      Save {s.savings}
                    </span>
                  </div>
                  <p className="text-gray-500 text-[15px] leading-relaxed mb-6">{s.desc}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {s.benefits.map((b, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-gray-500">
                        <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/compare?service=${s.link}`}>
                    <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-4 text-base" data-testid={`service-compare-${s.link}`}>
                      Compare Now <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Business Section with image */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="text-sm font-medium text-[#FF6600] tracking-widest uppercase mb-3 block">For Business</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2E2E33] tracking-tight mb-4">Business Energy Solutions</h2>
              <p className="text-base text-gray-500 leading-relaxed">
                Specialised energy comparison for businesses of all sizes across Australia. From small cafes to large commercial operations.
              </p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <img src={IMG_BIZ} alt="Business team" className="rounded-2xl shadow-xl w-full h-[300px] object-cover" />
            </motion.div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {BUSINESS_SERVICES.map((s, i) => (
              <motion.div
                key={i}
                className="bg-white border-2 border-[#E2E8F0] rounded-3xl p-8 card-lift"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#0B203B] flex items-center justify-center mb-5">
                  <s.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-extrabold text-[#2E2E33] text-xl mb-3">{s.title}</h3>
                <p className="text-gray-500 text-[15px] leading-relaxed mb-5">{s.desc}</p>
                <Link to={`/compare?service=${s.link}`}>
                  <Button variant="outline" className="border-2 border-[#2E2E33] text-[#2E2E33] font-bold rounded-full hover:bg-[#2E2E33] hover:text-white" data-testid={`biz-service-${i}`}>
                    Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with background */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 bg-[#2E2E33]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5 tracking-tight">Not Sure Where to Start?</h2>
          <p className="text-base text-white/60 mb-10">Speak with one of our comparison specialists - we'll help you identify where you can save the most.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/compare">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-10 py-4 text-lg" data-testid="services-cta-compare">
                Start a Comparison <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:1300796797">
              <Button variant="outline" className="border-2 border-white/30 text-white font-bold rounded-full px-10 py-4 text-lg hover:bg-white hover:text-[#2E2E33]" data-testid="services-cta-call">
                <Phone className="w-5 h-5 mr-2" /> 1300 796 797
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
